/**
 * Recording a commitment — `contact_lifecycle.commitment_at` and
 * `commitment_note`, which until now could be read everywhere and set nowhere.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * A PAUSE IS US DECIDING TO STOP. A COMMITMENT IS THEM ASKING US TO WAIT.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Both suppress advance_enrollments() and both push `contact_health.due_at`
 * out, so it is tempting to call them one control with two labels. They are
 * not, and the difference is measurable in the views — read from the live
 * definitions 2026-09-04, not from a migration file:
 *
 *     contact_health.is_paused := cl.paused_until is not null
 *                                 and cl.paused_until >= current_date
 *     contacts_no_next_action  := ... and not is_paused
 *
 * `commitment_at` is nowhere in `is_paused`. So a PAUSED contact drops out of
 * `contacts_no_next_action` altogether — permission to stop looking, which is
 * the right instrument when you genuinely are not working them. A COMMITTED
 * contact stays in it: still on the board, still yours, simply not reported
 * late before the date you actually promised.
 *
 * That is why substituting one for the other loses people. Without this
 * control there were only two moves — be told you are overdue for a fortnight
 * you deliberately agreed to, or pause the contact and stop seeing them —
 * and the second is how somebody goes quiet for nine months.
 *
 * WHY THIS IS A DATE AND A NOTE, AND NOT A TASK
 * ---------------------------------------------
 * atomic#7 left the shape open: a commitment might be derived from a task's
 * due date rather than stored as two more columns, since `tasks.contact_id`
 * and `open_tasks` already exist. The user settled it — a date and a note
 * beside the pause control — and the columns are the ones `contact_health`,
 * `advance_enrollments()` and the Follow-ups queue already read. Deriving it
 * from a task would mean changing all three to answer a question they
 * currently answer correctly. Recorded rather than quietly decided; if the
 * derivation is ever wanted, this file is the one place that writes it.
 *
 * WHY IT IS NOT IN lifecycleRules.ts BESIDE validatePause()
 * ----------------------------------------------------------
 * It would fit there, and the write goes through that file's saveLifecycle()
 * exactly as `paused_until` does. It is separate because the whole point of
 * the pair is that they are two decisions with two meanings, and the one
 * mistake to design against is somebody reaching for the nearer function.
 * A `*Rules`-shaped module per concern is this directory's convention —
 * countsAsTouch.ts, mailto.ts, templates.ts — not a new one.
 */

import type { DataProvider, Identifier } from "ra-core";

import { saveLifecycle } from "./lifecycleRules";

/**
 * A chosen calendar day, as the instant the hold lifts.
 *
 * THE START OF THE DAY, NOT THE END, and the generator is what decides it.
 * A commitment holds while `commitment_at > now()`, so storing the start of
 * Thursday means the hold lifts as Thursday begins: that day's nightly run
 * writes the task, and `contact_health.due_at` — greatest() over the clock,
 * the commitment and the pause — reports the contact due ON Thursday. Which
 * is what "they asked me to call Thursday" means. End-of-day would hold
 * through Thursday and surface them on Friday, a day after the call.
 *
 * THE PARTS ARE READ AS LOCAL. `new Date("2026-09-24")` is UTC midnight — the
 * 23rd at 8pm in New York — so writing the date input straight through would
 * lift the hold the evening BEFORE the day somebody promised, and the drip
 * would land the night before the call. Same frame as dateInputValue() in
 * touchEdit.ts and retimedDueAt() in planRules.ts, so a round trip through
 * the date input is the identity rather than a one-day drift per save.
 */
export function commitmentInstant(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0).toISOString();
}

export type CommitmentCheck =
  | { ok: true; at: string; note: string | null }
  | { ok: false; reason: string };

/**
 * Can this be a commitment?
 *
 * A BLANK DATE IS REFUSED, for validatePause()'s reason: an undated promise
 * is not a promise, it is "I'll get to it", which is the state this whole
 * system exists to end.
 *
 * TODAY IS REFUSED, AND THIS IS THE ONE BOUNDARY THAT DIFFERS FROM A PAUSE.
 * validatePause() allows today because `paused_until` is a DATE tested
 * `>= current_date` — pausing until today buys the rest of today. A
 * commitment is a TIMESTAMPTZ tested `commitment_at <= now()`, so today at
 * local midnight is already behind us: it would suppress nothing, while
 * greatest() would still take it into `due_at` and report the contact overdue
 * from the moment it was saved. It is the one date that makes the record
 * worse than doing nothing, so it is refused with the reason rather than
 * stored and wondered about.
 *
 * THE NOTE IS OPTIONAL, matching `paused_reason`. A blank one is stored as
 * NULL and not as "": the panel prints `— {note}` whenever a note exists, so
 * an empty string renders as a stray dash promising a sentence that is not
 * there.
 */
export function validateCommitment(
  day: string | null | undefined,
  note: string | null | undefined,
  today: string,
): CommitmentCheck {
  const chosen = (day ?? "").trim();
  if (!chosen)
    return {
      ok: false,
      reason:
        "A commitment needs the date they asked for. Without one there is nothing to be held to, and nothing to expire.",
    };
  if (chosen < today)
    return {
      ok: false,
      reason: `${chosen} has already passed, so nothing would be waiting for it.`,
    };
  if (chosen === today)
    return {
      ok: false,
      reason:
        "A commitment dated today is already behind the clock — it would hold nothing back and would report this contact as overdue the moment you saved it. If you owe them something today, that is a task, not a commitment.",
    };
  return {
    ok: true,
    at: commitmentInstant(chosen),
    note: (note ?? "").trim() || null,
  };
}

/**
 * Record it. THE SAME PATH `paused_until` TAKES, on purpose.
 *
 * saveLifecycle() upserts, because a contact with no `contact_lifecycle` row
 * is the normal untriaged state — `contact_health` left-joins for exactly
 * that reason — and that person is the one somebody is most likely to be
 * making a first commitment to. An update-only write would fail on the click
 * that was finally putting them on the board.
 */
export async function saveCommitment(
  dataProvider: DataProvider,
  contactId: Identifier,
  day: string | null | undefined,
  note: string | null | undefined,
  today: string,
): Promise<CommitmentCheck & { ok: true }> {
  const check = validateCommitment(day, note, today);
  if (!check.ok) throw new Error(check.reason);

  await saveLifecycle(dataProvider, contactId, {
    commitment_at: check.at,
    commitment_note: check.note,
  });
  return check;
}

/**
 * Drop it — the call happened, or it is not happening.
 *
 * BOTH COLUMNS, NEVER JUST THE DATE. A note with no `commitment_at` is a
 * sentence about a promise the record no longer holds; nothing displays it,
 * because every reader prints it beside the date, so it would sit in the
 * table invisibly and then reappear attached to the NEXT commitment somebody
 * made — a quote from the wrong conversation, months later, with nothing to
 * suggest it.
 *
 * There is no verdict function here. Clearing a commitment that has already
 * elapsed is legitimate housekeeping, and clearing one that is not there
 * writes two nulls over two nulls.
 */
export async function clearCommitment(
  dataProvider: DataProvider,
  contactId: Identifier,
): Promise<void> {
  await saveLifecycle(dataProvider, contactId, {
    commitment_at: null,
    commitment_note: null,
  });
}
